import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import toast from "react-hot-toast";
import {
  MailIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  OfficeBuildingIcon,
  BriefcaseIcon,
  LockClosedIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/solid";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

import AdvanceEmploymentEditing from "./AdvanceEmploymentEditing";
import AdvanceDepartmentEditing from "./AdvanceDepartmentEditing";
import EmploymentTypeModal from "./EmploymentTypeModal";
import EmployeeServicesModal from "./EmployeeServicesModal";
import GeneratePasswordModal from "./GeneratePasswordModal";
import Spinner from "../../components/loader/Spinner";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import LinkButton from "../../components/ui/LinkButton";
import RoundedContainer from "../../components/containers/RoundedContainer";
import { Button } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

import useGet from "../../hooks/read/useGet";
import {
  getAttendanceAndLeavesStatistics,
  getPayrollStatistics,
  getApplicationSettingsOverview,
} from "../../api/employeeStats";

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SingleEmployeeDetail = () => {
  const { company_employee_id, username } = useParams();

  const [activeTab, setActiveTab] = useState("profile"); // profile | attendance | payroll | settings
  const [period, setPeriod] = useState("this_month");
  const [monthYear, setMonthYear] = useState("");

  // Modals
  const [isOpenAdvanceEmployment, setIsOpenAdvanceEmployment] = useState(false);
  const [isOpenAdvanceDepartment, setIsOpenAdvanceDepartment] = useState(false);
  const [isEmploymentTypeModalOpen, setIsEmploymentTypeModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isGeneratePasswordModalOpen, setIsGeneratePasswordModalOpen] = useState(false);

  // States for Stats API Data
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const [payrollData, setPayrollData] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);

  const [settingsData, setSettingsData] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Base Employee Details API
  const { list } = useGet({
    url: "employeedetails_edit",
    initialData: { company_employee_id },
    onError: (err) => {
      const { status, data } = err.response || {};
      if (status >= 400 && status <= 499) {
        toast.error(data?.message || "Failed to fetch details");
      } else {
        toast.error("Internal Server Error");
      }
    },
  });

  // Fetch Attendance & Leaves Stats
  const fetchAttendanceStats = useCallback(
    async (selectedPeriod = period, selectedMonthYear = monthYear) => {
      setAttendanceLoading(true);
      try {
        const params = {
          company_employee_id,
          period: selectedPeriod,
        };
        if (selectedPeriod === "custom_month" && selectedMonthYear) {
          params.month_year = selectedMonthYear;
        }
        const res = await getAttendanceAndLeavesStatistics(params);
        if (res?.data?.status === 200 || res?.data?.data) {
          setAttendanceData(res.data.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setAttendanceLoading(false);
      }
    },
    [company_employee_id, period, monthYear]
  );

  // Fetch Payroll Stats
  const fetchPayroll = useCallback(async () => {
    setPayrollLoading(true);
    try {
      const res = await getPayrollStatistics({ company_employee_id });
      if (res?.data?.status === 200 || res?.data?.data) {
        setPayrollData(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setPayrollLoading(false);
    }
  }, [company_employee_id]);

  // Fetch Settings Overview
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await getApplicationSettingsOverview({ company_employee_id });
      if (res?.data?.status === 200 || res?.data?.data) {
        setSettingsData(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSettingsLoading(false);
    }
  }, [company_employee_id]);

  useEffect(() => {
    if (activeTab === "attendance") {
      fetchAttendanceStats(period, monthYear);
    } else if (activeTab === "payroll") {
      fetchPayroll();
    } else if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab, period, monthYear, fetchAttendanceStats, fetchPayroll, fetchSettings]);

  const breadcrumbs = [
    { label: "Employees", link: "/employee-details" },
    { label: "Employee Details", link: "" },
  ];

  const constructstringbasedonyears = (year) => {
    return +year === 1 ? `${year} year` : `${year} years`;
  };

  const { personalDetail, bankDetail, addressDetail, workDetail, statutory } = list?.data?.data || {};
  const type = list?.data?.data?.type_of_employee || "Employee";

  // Tab Header Items (Strictly excluding Documents tab)
  const tabs = [
    { id: "profile", label: "Profile" },
    { id: "attendance", label: "Attendance & Leaves" },
    { id: "payroll", label: "Payroll" },
    { id: "settings", label: "Application Setting" },
  ];

  // --- CHART DATA CONFIGURATIONS ---

  // 1. Daily Attendance Bar Chart Data
  const dailyAttendanceList = attendanceData?.attendance_overview?.daily_attendance || [];
  const getAttendanceBarColor = (status) => {
    switch (status?.toLowerCase()) {
      case "present":
        return "#3b82f6";
      case "remote":
        return "#93c5fd";
      case "absent":
        return "#ef4444";
      case "half day":
      case "half_day":
        return "#f59e0b";
      case "holiday":
      case "week off":
      case "weekend":
        return "#cbd5e1";
      case "leave":
        return "#a855f7";
      default:
        return "#cbd5e1";
    }
  };

  const dailyAttendanceChartData = {
    labels: dailyAttendanceList.map((d) => d.day_name || d.date),
    datasets: [
      {
        label: "Worked Hours",
        data: dailyAttendanceList.map((d) => parseFloat(d.total_worked_hours) || 0),
        backgroundColor: dailyAttendanceList.map((d) => getAttendanceBarColor(d.status)),
        borderRadius: 4,
        minBarLength: 14,
      },
    ],
  };

  // 2. Attendance Breakdown Donut Chart Data
  const breakdownObj = attendanceData?.attendance_overview?.attendance_breakdown || {
    present: 0,
    remote: 0,
    absent: 0,
    holiday: 0,
  };
  const attendanceBreakdownChartData = {
    labels: Object.keys(breakdownObj).map((k) => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [
      {
        data: Object.values(breakdownObj),
        backgroundColor: ["#3b82f6", "#93c5fd", "#ef4444", "#cbd5e1"],
        borderWidth: 0,
      },
    ],
  };

  // 3. Leave Type Distribution Doughnut Chart Data
  const leaveDist = attendanceData?.leaves_overview?.leave_type_distribution || [];
  const leaveDistributionChartData = {
    labels: leaveDist.map((l) => l.type),
    datasets: [
      {
        data: leaveDist.map((l) => l.days),
        backgroundColor: ["#34d399", "#60a5fa", "#f59e0b", "#c084fc", "#f43f5e", "#8b5cf6"],
        borderWidth: 0,
      },
    ],
  };

  // 4. Monthly Leave Trend Line Chart Data
  const leaveTrendList = attendanceData?.leaves_overview?.monthly_leave_trend || [];
  const monthlyLeaveTrendChartData = {
    labels: leaveTrendList.map((m) => m.month),
    datasets: [
      {
        label: "Days Taken",
        data: leaveTrendList.map((m) => m.days_taken),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "Balance",
        data: leaveTrendList.map((m) => m.balance),
        borderColor: "#10b981",
        borderDash: [5, 5],
        tension: 0.4,
      },
    ],
  };

  // 5. Remote Work Monthly Trend Bar Chart Data
  const remoteTrendList = attendanceData?.remote_and_regularization?.remote_monthly_trend || [];
  const remoteTrendChartData = {
    labels: remoteTrendList.map((r) => r.month),
    datasets: [
      {
        label: "Remote Days",
        data: remoteTrendList.map((r) => r.days),
        backgroundColor: "#a5b4fc",
        borderRadius: 6,
      },
    ],
  };

  return (
    <>
      {list.isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="px-4 py-3">
            <BreadCrumbs crumbs={breadcrumbs} />
          </div>

          {/* Employee Header Banner */}
          <div className="mx-4 bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-full bg-blue-600 text-white font-bold text-xl flex items-center justify-center">
                {personalDetail?.firstName?.charAt(0)}
                {personalDetail?.lastName?.charAt(0)}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {personalDetail?.firstName} {personalDetail?.lastName}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-500 mt-1 flex-wrap">
                  {personalDetail?.email && (
                    <span className="flex items-center gap-1">
                      <MailIcon className="w-4 h-4 text-gray-400" />
                      {personalDetail.email}
                    </span>
                  )}
                  {personalDetail?.mobileNumber && (
                    <span className="flex items-center gap-1">
                      <PhoneIcon className="w-4 h-4 text-gray-400" />
                      +91 {personalDetail.mobileNumber}
                    </span>
                  )}
                  {workDetail?.currentWorkDetail?.dateOfJoining && (
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <Moment unix format="DD MMM YY">
                        {workDetail.currentWorkDetail.dateOfJoining}
                      </Moment>
                    </span>
                  )}
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                    {type}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <LinkButton
                to={`/employee-details/${company_employee_id}/investments-and-proofs`}
                className="bg-indigo-100 text-indigo-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-indigo-200"
              >
                Investments & Proofs
              </LinkButton>
              <Button
                onClick={() => setIsEmploymentTypeModalOpen(true)}
                className="bg-purple-100 text-purple-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-purple-200"
              >
                Employment Type
              </Button>
              <Button
                onClick={() => setIsOpenAdvanceDepartment(true)}
                className="bg-blue-100 text-blue-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-blue-200"
              >
                Departments
              </Button>
              <Button
                onClick={() => setIsOpenAdvanceEmployment(true)}
                className="bg-orange-100 text-orange-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-orange-200"
              >
                Shift Details
              </Button>
              <Button
                onClick={() => setIsGeneratePasswordModalOpen(true)}
                className="bg-emerald-100 text-emerald-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-emerald-200"
              >
                Password
              </Button>
              <LinkButton
                to={`/employee-salary-details/${company_employee_id}/${username}`}
                className="bg-amber-100 text-amber-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-amber-200"
              >
                Salary Details
              </LinkButton>
              <LinkButton
                to={`/resign-employee/${company_employee_id}/${username}`}
                className="bg-red-100 text-red-700 font-semibold text-xs py-2 px-3 rounded-md hover:bg-red-200"
              >
                Resign
              </LinkButton>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mx-4 border-b border-gray-200 mb-6 bg-white rounded-t-lg px-2 pt-2 flex space-x-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600 font-semibold"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* TAB 1: PROFILE DETAILS */}
          {activeTab === "profile" && (
            <div className="px-4 space-y-4 my-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Personal Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Personal details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">First Name</TableCell>
                        <TableCell className="text-sm py-3 px-4">{personalDetail?.firstName || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Last Name</TableCell>
                        <TableCell className="text-sm py-3 px-4">{personalDetail?.lastName || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Gender</TableCell>
                        <TableCell className="text-sm py-3 px-4 capitalize">{personalDetail?.gender || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Email</TableCell>
                        <TableCell className="text-sm py-3 px-4 text-blue-600">{personalDetail?.email || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Date of birth</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {personalDetail?.dateOfBirth ? (
                            <Moment unix format="DD/MM/YYYY" date={personalDetail.dateOfBirth} />
                          ) : (
                            "----"
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Mobile number</TableCell>
                        <TableCell className="text-sm py-3 px-4">{personalDetail?.mobileNumber || "----"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Permanent Address Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Permanent address details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Address line 1</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.permanentAddress?.addressLine1 || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Address line 2</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.permanentAddress?.addressLine2 || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Pincode</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.permanentAddress?.pincode || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">City</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.permanentAddress?.city || "----"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Current Address Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Current address details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Address line 1</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.currentAddress?.addressLine1 || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Address line 2</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.currentAddress?.addressLine2 || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Pincode</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.currentAddress?.pincode || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">City</TableCell>
                        <TableCell className="text-sm py-3 px-4">{addressDetail?.currentAddress?.city || "----"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Bank Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Bank details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Bank name</TableCell>
                        <TableCell className="text-sm py-3 px-4">{bankDetail?.bankName || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Account number</TableCell>
                        <TableCell className="text-sm py-3 px-4">{bankDetail?.accountNumber || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">IFSC code</TableCell>
                        <TableCell className="text-sm py-3 px-4">{bankDetail?.ifsc || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">PAN number</TableCell>
                        <TableCell className="text-sm py-3 px-4">{bankDetail?.panNumber || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Aadhaar number</TableCell>
                        <TableCell className="text-sm py-3 px-4">{bankDetail?.adhaarNumber || "----"}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Previous Work Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Previous work details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Educational qualification</TableCell>
                        <TableCell className="text-sm py-3 px-4">{workDetail?.previousWorkDetail?.education || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Work experience</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {constructstringbasedonyears(+workDetail?.previousWorkDetail?.workExperience || 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Current Work Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Current work details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Designation</TableCell>
                        <TableCell className="text-sm py-3 px-4">{workDetail?.currentWorkDetail?.designation || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Employee code</TableCell>
                        <TableCell className="text-sm py-3 px-4">{workDetail?.currentWorkDetail?.employeeCode || "----"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Date of joining</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {workDetail?.currentWorkDetail?.dateOfJoining ? (
                            <Moment unix format="DD/MM/YYYY" date={workDetail.currentWorkDetail.dateOfJoining} />
                          ) : (
                            "----"
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">Probation date</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {workDetail?.currentWorkDetail?.probationDate ? (
                            <Moment unix format="DD/MM/YYYY" date={workDetail.currentWorkDetail.probationDate} />
                          ) : (
                            "----"
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>

                {/* Statutory Details */}
                <RoundedContainer>
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell colSpan={2} className="font-bold text-base">
                        Statutory details
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">PF account number</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {workDetail?.statutory?.employeeProvidentFund?.providentFundNumber ||
                            statutory?.employeeProvidentFund?.providentFundNumber ||
                            "----"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">UAN</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {workDetail?.statutory?.employeeProvidentFund?.UAN ||
                            statutory?.employeeProvidentFund?.UAN ||
                            "----"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-bold py-3 px-4 text-xs">ESIC number</TableCell>
                        <TableCell className="text-sm py-3 px-4">
                          {workDetail?.statutory?.employeeStateIndurance?.induranceNumber ||
                            statutory?.employeeStateIndurance?.induranceNumber ||
                            "----"}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RoundedContainer>
              </div>
            </div>
          )}

          {/* TAB 2: ATTENDANCE & LEAVES WITH CHARTS */}
          {activeTab === "attendance" && (
            <div className="px-4 space-y-6 my-4">
              {/* Period Selector Header */}
              <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold text-gray-700">Period:</span>
                  {["this_month", "last_3_months", "last_6_months", "this_year"].map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        setPeriod(p);
                        setMonthYear("");
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                        period === p
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {p.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500">Custom Month:</span>
                  <input
                    type="month"
                    className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 focus:outline-none focus:border-blue-500"
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m] = e.target.value.split("-");
                        setMonthYear(`${m}-${y}`);
                        setPeriod("custom_month");
                      }
                    }}
                  />
                </div>
              </div>

              {attendanceLoading ? (
                <Spinner />
              ) : (
                <>
                  {/* ATTENDANCE OVERVIEW SECTION */}
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                      Attendance Overview
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-green-50/60 border border-green-100">
                        <span className="text-xs text-gray-500 block font-medium">Present days</span>
                        <div className="text-3xl font-extrabold text-green-600 mt-1">
                          {attendanceData?.attendance_overview?.present_days ?? 0}
                        </div>
                        {attendanceData?.attendance_overview?.present_change && (
                          <span className="text-xs font-semibold text-green-600 mt-1 block">
                            {attendanceData.attendance_overview.present_change} vs last period
                          </span>
                        )}
                      </div>

                      <div className="p-4 rounded-lg bg-red-50/60 border border-red-100">
                        <span className="text-xs text-gray-500 block font-medium">Absent days</span>
                        <div className="text-3xl font-extrabold text-red-600 mt-1">
                          {attendanceData?.attendance_overview?.absent_days ?? 0}
                        </div>
                        {attendanceData?.attendance_overview?.absent_change && (
                          <span className="text-xs font-semibold text-red-600 mt-1 block">
                            {attendanceData.attendance_overview.absent_change} vs last period
                          </span>
                        )}
                      </div>

                      <div className="p-4 rounded-lg bg-blue-50/60 border border-blue-100">
                        <span className="text-xs text-gray-500 block font-medium">Avg login time</span>
                        <div className="text-2xl font-bold text-gray-800 mt-1">
                          {attendanceData?.attendance_overview?.avg_login_time || "--:--"}
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">
                          Expected {attendanceData?.attendance_overview?.expected_login_time || "09:00 AM"}
                        </span>
                      </div>

                      <div className="p-4 rounded-lg bg-purple-50/60 border border-purple-100">
                        <span className="text-xs text-gray-500 block font-medium">Avg work hours</span>
                        <div className="text-2xl font-bold text-gray-800 mt-1">
                          {attendanceData?.attendance_overview?.avg_work_hours ?? 0} hrs
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">
                          Target {attendanceData?.attendance_overview?.target_work_hours ?? 8} hrs
                        </span>
                      </div>
                    </div>

                    {/* ATTENDANCE CHARTS ROW */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Daily Attendance Bar Chart */}
                      <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                          <h5 className="text-xs font-bold text-gray-700">Daily Attendance</h5>
                          <div className="flex items-center flex-wrap gap-2.5 text-[11px] text-gray-600">
                            <span className="flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-sm bg-[#3b82f6] inline-block" />
                              Present
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-sm bg-[#93c5fd] inline-block" />
                              Remote
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-sm bg-[#ef4444] inline-block" />
                              Absent
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-sm bg-[#cbd5e1] inline-block" />
                              Holiday
                            </span>
                          </div>
                        </div>
                        <div className="h-56">
                          <Bar
                            data={dailyAttendanceChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    title: (items) => {
                                      if (!items.length) return "";
                                      const index = items[0].dataIndex;
                                      const item = dailyAttendanceList[index];
                                      if (item?.date) {
                                        return item.day_name ? `${item.day_name}, ${item.date}` : item.date;
                                      }
                                      return items[0].label || "";
                                    },
                                    label: (context) => {
                                      const item = dailyAttendanceList[context.dataIndex];
                                      const hours =
                                        item?.total_worked_hours !== undefined &&
                                        item?.total_worked_hours !== null &&
                                        item?.total_worked_hours !== ""
                                          ? item.total_worked_hours
                                          : 0;
                                      return [
                                        `Total Worked Hours: ${hours} hrs`,
                                        `Status: ${item?.status || "N/A"}`,
                                      ];
                                    },
                                  },
                                },
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  grid: { color: "#f3f4f6" },
                                },
                                x: { grid: { display: false } },
                              },
                            }}
                          />
                        </div>
                      </div>

                      {/* Attendance Breakdown Donut Chart */}
                      <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50 flex flex-col items-center">
                        <h5 className="text-xs font-bold text-gray-700 mb-3 self-start">
                          Attendance Breakdown
                        </h5>
                        <div className="h-52 w-52">
                          <Doughnut
                            data={attendanceBreakdownChartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "right" } },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LEAVES OVERVIEW SECTION */}
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                      Leaves Overview
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-blue-50/50 border border-blue-100">
                        <span className="text-xs text-gray-500 font-medium">Total Leave Balance</span>
                        <div className="text-3xl font-extrabold text-blue-700 mt-1">
                          {attendanceData?.leaves_overview?.total_leave_balance ?? 0} days
                        </div>
                      </div>

                      <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
                        <span className="text-xs text-gray-500 font-medium">Leaves Taken</span>
                        <div className="text-3xl font-extrabold text-amber-600 mt-1">
                          {attendanceData?.leaves_overview?.leaves_taken ?? 0} days
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">
                          {attendanceData?.leaves_overview?.leaves_taken_approved ?? 0} approved, {attendanceData?.leaves_overview?.leaves_taken_pending ?? 0} pending
                        </span>
                      </div>

                      <div className="p-4 rounded-lg bg-emerald-50/50 border border-emerald-100">
                        <span className="text-xs text-gray-500 font-medium">Leaves Remaining</span>
                        <div className="text-3xl font-extrabold text-emerald-600 mt-1">
                          {attendanceData?.leaves_overview?.leaves_remaining ?? 0} days
                        </div>
                      </div>

                      <div className="p-4 rounded-lg bg-rose-50/50 border border-rose-100">
                        <span className="text-xs text-gray-500 font-medium">LOP Days</span>
                        <div className="text-3xl font-extrabold text-rose-600 mt-1">
                          {attendanceData?.leaves_overview?.lop_days ?? 0} days
                        </div>
                        <span className="text-xs text-rose-400 mt-1 block">Loss of pay</span>
                      </div>
                    </div>

                    {/* LEAVE CHARTS ROW */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {/* Leave Type Distribution Doughnut Chart */}
                      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 flex flex-col items-center">
                        <h5 className="text-xs font-bold text-gray-700 mb-3 self-start">
                          Leave Type Distribution
                        </h5>
                        <div className="h-52 w-52 flex items-center justify-center">
                          {leaveDist.length > 0 ? (
                            <Doughnut
                              data={leaveDistributionChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: "right" } },
                              }}
                            />
                          ) : (
                            <span className="text-xs text-gray-400">No leave records available</span>
                          )}
                        </div>
                      </div>

                      {/* Monthly Leave Trend Line Chart */}
                      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                        <h5 className="text-xs font-bold text-gray-700 mb-3">Monthly Leave Trend</h5>
                        <div className="h-56 flex items-center justify-center">
                          {leaveTrendList.length > 0 ? (
                            <Line
                              data={monthlyLeaveTrendChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: "bottom" } },
                                scales: {
                                  y: { beginAtZero: true },
                                },
                              }}
                            />
                          ) : (
                            <span className="text-xs text-gray-400">No trend data available</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* REMOTE WORK & REGULARIZATION SECTION WITH TREND GRAPH */}
                  {attendanceData?.remote_and_regularization && (
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                        Remote Work & Regularization
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-indigo-50/50 border border-indigo-100">
                          <span className="text-xs text-gray-500 font-medium">Remote Days</span>
                          <div className="text-3xl font-extrabold text-indigo-700 mt-1">
                            {attendanceData.remote_and_regularization.remote_days ?? 0} days
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-teal-50/50 border border-teal-100">
                          <span className="text-xs text-gray-500 font-medium">Remote Approval Rate</span>
                          <div className="text-3xl font-extrabold text-teal-700 mt-1">
                            {attendanceData.remote_and_regularization.remote_approval_rate ?? 0}%
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-orange-50/50 border border-orange-100">
                          <span className="text-xs text-gray-500 font-medium">Regularizations</span>
                          <div className="text-3xl font-extrabold text-orange-700 mt-1">
                            {attendanceData.remote_and_regularization.regularizations ?? 0}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-purple-50/50 border border-purple-100">
                          <span className="text-xs text-gray-500 font-medium">Reg. Approval Rate</span>
                          <div className="text-3xl font-extrabold text-purple-700 mt-1">
                            {attendanceData.remote_and_regularization.reg_approval_rate ?? 0}%
                          </div>
                        </div>
                      </div>

                      {/* Remote Work Monthly Trend Bar Chart */}
                      <div className="mt-6 border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                        <h5 className="text-xs font-bold text-gray-700 mb-3">
                          Remote Work — Monthly Trend
                        </h5>
                        <div className="h-48 flex items-center justify-center">
                          {remoteTrendList.length > 0 ? (
                            <Bar
                              data={remoteTrendChartData}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: { y: { beginAtZero: true } },
                              }}
                            />
                          ) : (
                            <span className="text-xs text-gray-400">No remote trend data available</span>
                          )}
                        </div>
                      </div>

                      {/* Recent Regularization Requests Table */}
                      <div className="mt-6 border border-gray-100 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 font-bold text-xs text-gray-700 border-b border-gray-100">
                          Recent Regularization Requests
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs text-gray-600">
                            <thead className="bg-gray-50 text-gray-700 uppercase font-semibold border-b">
                              <tr>
                                <th className="py-2.5 px-4">Date</th>
                                <th className="py-2.5 px-4">Reason</th>
                                <th className="py-2.5 px-4">Check-In</th>
                                <th className="py-2.5 px-4">Check-Out</th>
                                <th className="py-2.5 px-4">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {(
                                attendanceData.remote_and_regularization
                                  .recent_regularization_requests || []
                              ).map((req, idx) => (
                                <tr key={idx} className="hover:bg-gray-50/50">
                                  <td className="py-2.5 px-4 font-semibold text-gray-800">
                                    {req.date}
                                  </td>
                                  <td className="py-2.5 px-4">{req.reason}</td>
                                  <td className="py-2.5 px-4">{req.check_in}</td>
                                  <td className="py-2.5 px-4">{req.check_out}</td>
                                  <td className="py-2.5 px-4">
                                    <span
                                      className={`px-2 py-0.5 rounded-full font-semibold ${
                                        req.status === "Approved"
                                          ? "bg-green-100 text-green-700"
                                          : "bg-amber-100 text-amber-700"
                                      }`}
                                    >
                                      {req.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              {!attendanceData.remote_and_regularization
                                .recent_regularization_requests?.length && (
                                <tr>
                                  <td colSpan={5} className="py-3 text-center text-gray-400">
                                    No regularization requests found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* TAB 3: PAYROLL */}
          {activeTab === "payroll" && (
            <div className="px-4 space-y-6 my-4">
              {payrollLoading ? (
                <Spinner />
              ) : (
                <>
                  {/* 1. SALARY SUMMARY */}
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                      1. Salary Summary
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-emerald-50/60 border border-emerald-100">
                        <span className="text-xs text-gray-500 font-medium">Annual CTC</span>
                        <div className="text-2xl font-extrabold text-emerald-700 mt-1">
                          ₹{(payrollData?.salary_summary?.annual_ctc ?? 0).toLocaleString()}
                        </div>
                        {payrollData?.salary_summary?.ctc_change_percentage && (
                          <span className="text-xs font-semibold text-emerald-600 mt-1 block">
                            +{payrollData.salary_summary.ctc_change_percentage}% from last revision
                          </span>
                        )}
                      </div>

                      <div className="p-4 rounded-lg bg-blue-50/60 border border-blue-100">
                        <span className="text-xs text-gray-500 font-medium">Monthly In-Hand</span>
                        <div className="text-2xl font-extrabold text-blue-700 mt-1">
                          ₹{(payrollData?.salary_summary?.monthly_in_hand ?? 0).toLocaleString()}
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">After all deductions</span>
                      </div>

                      <div className="p-4 rounded-lg bg-indigo-50/60 border border-indigo-100">
                        <span className="text-xs text-gray-500 font-medium">Last Disbursed</span>
                        <div className="text-2xl font-bold text-gray-800 mt-1">
                          ₹{(payrollData?.salary_summary?.last_disbursed?.amount ?? 0).toLocaleString()}
                        </div>
                        <span className="text-xs text-emerald-600 font-medium mt-1 block">
                          {payrollData?.salary_summary?.last_disbursed?.date || "N/A"} ({payrollData?.salary_summary?.last_disbursed?.status || "Paid"})
                        </span>
                      </div>

                      <div className="p-4 rounded-lg bg-purple-50/60 border border-purple-100">
                        <span className="text-xs text-gray-500 font-medium">Next Payout</span>
                        <div className="text-2xl font-bold text-gray-800 mt-1">
                          ₹{(payrollData?.salary_summary?.next_payout?.amount ?? 0).toLocaleString()}
                        </div>
                        <span className="text-xs text-purple-600 font-medium mt-1 block">
                          Expected {payrollData?.salary_summary?.next_payout?.date || "End of Month"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 2. SALARY STRUCTURE & BREAKDOWN */}
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                      2. Salary Structure
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Earnings List */}
                      <div className="border border-green-100 bg-green-50/30 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-bold text-sm text-green-700">Earnings</span>
                          <span className="font-extrabold text-sm text-green-700">
                            ₹{(payrollData?.salary_structure?.gross_earnings ?? 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-2 text-xs text-gray-600">
                          {Array.isArray(payrollData?.salary_structure?.earnings) &&
                          payrollData.salary_structure.earnings.length > 0 ? (
                            payrollData.salary_structure.earnings.map((e, idx) => (
                              <div key={idx} className="flex justify-between border-b border-green-100/50 py-1">
                                <span>{e.name}</span>
                                <span className="font-semibold text-gray-800">₹{(e.amount ?? 0).toLocaleString()}</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">No earnings configured</span>
                          )}
                        </div>
                      </div>

                      {/* Deductions List */}
                      <div className="border border-red-100 bg-red-50/30 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-bold text-sm text-red-700">Deductions</span>
                          <span className="font-extrabold text-sm text-red-700">
                            ₹{(payrollData?.salary_structure?.total_deductions ?? 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-2 text-xs text-gray-600">
                          {Array.isArray(payrollData?.salary_structure?.deductions) &&
                          payrollData.salary_structure.deductions.length > 0 ? (
                            payrollData.salary_structure.deductions.map((d, idx) => (
                              <div key={idx} className="flex justify-between border-b border-red-100/50 py-1">
                                <span>{d.name}</span>
                                <span className="font-semibold text-gray-800">₹{(d.amount ?? 0).toLocaleString()}</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">No deductions configured</span>
                          )}
                        </div>
                      </div>

                      {/* Net Take-Home */}
                      <div className="border border-blue-100 bg-blue-50/30 p-4 rounded-lg flex flex-col justify-between">
                        <div>
                          <span className="font-bold text-xs text-blue-600 uppercase tracking-wider block mb-1">
                            Net Take-Home
                          </span>
                          <div className="text-3xl font-extrabold text-blue-700">
                            ₹{(payrollData?.salary_structure?.net_take_home ?? 0).toLocaleString()}
                          </div>
                          <span className="text-xs text-gray-400 block mt-1">Monthly net pay</span>
                        </div>
                        <div className="mt-4 pt-3 border-t border-blue-200/50 flex justify-between text-xs text-gray-600">
                          <span>Gross: ₹{(payrollData?.salary_structure?.gross_earnings ?? 0).toLocaleString()}</span>
                          <span>Deductions: ₹{(payrollData?.salary_structure?.total_deductions ?? 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3. YEAR-TO-DATE SUMMARY */}
                  {payrollData?.ytd_summary && (
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                        3. Year-to-date Summary ({payrollData.ytd_summary.financial_year || "Current FY"})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <span className="text-xs text-gray-500 font-medium">Total Gross Earned</span>
                          <div className="text-2xl font-bold text-gray-800 mt-1">
                            ₹{(payrollData.ytd_summary.total_gross_earned ?? 0).toLocaleString()}
                          </div>
                          <span className="text-xs text-gray-400 mt-1 block">
                            {payrollData.ytd_summary.months_completed ?? 0} months completed
                          </span>
                        </div>

                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <span className="text-xs text-gray-500 font-medium">Total Deductions</span>
                          <div className="text-2xl font-bold text-red-600 mt-1">
                            ₹{(payrollData.ytd_summary.total_deductions ?? 0).toLocaleString()}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <span className="text-xs text-gray-500 font-medium">Total Net Paid</span>
                          <div className="text-2xl font-bold text-green-600 mt-1">
                            ₹{(payrollData.ytd_summary.total_net_paid ?? 0).toLocaleString()}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <span className="text-xs text-gray-500 font-medium">Total TDS Deducted</span>
                          <div className="text-2xl font-bold text-amber-600 mt-1">
                            ₹{(payrollData.ytd_summary.total_tds_deducted ?? 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. MONTHLY PAYROLL HISTORY */}
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                      4. Monthly Payroll History
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs text-gray-600">
                        <thead className="bg-gray-50 text-gray-700 uppercase font-semibold border-b">
                          <tr>
                            <th className="py-3 px-4">Month</th>
                            <th className="py-3 px-4">Payment Date</th>
                            <th className="py-3 px-4">Gross</th>
                            <th className="py-3 px-4">Deductions</th>
                            <th className="py-3 px-4">Net Paid</th>
                            <th className="py-3 px-4">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(payrollData?.monthly_payroll_history || []).map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="py-3 px-4 font-semibold text-gray-800">{row.month}</td>
                              <td className="py-3 px-4">{row.payment_date}</td>
                              <td className="py-3 px-4">₹{(row.gross ?? 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-red-600">₹{(row.deductions ?? 0).toLocaleString()}</td>
                              <td className="py-3 px-4 font-bold text-green-600">
                                {row.net_paid !== null ? `₹${row.net_paid.toLocaleString()}` : "----"}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                                  row.status === "Paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                                }`}>
                                  {row.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {(!payrollData?.monthly_payroll_history?.length) && (
                            <tr>
                              <td colSpan={6} className="py-4 text-center text-gray-400">
                                No payroll history found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 4: APPLICATION SETTING */}
          {activeTab === "settings" && (
            <div className="px-4 space-y-6 my-4">
              {settingsLoading ? (
                <Spinner />
              ) : (
                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6">
                    Application Settings Overview
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shift Details */}
                    <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                      <h5 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-blue-600" />
                        Shift Details
                      </h5>
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex justify-between py-1 border-b border-gray-200/40">
                          <span className="font-medium text-gray-500">Shift Name</span>
                          <span className="font-semibold text-gray-800">{settingsData?.shift_details?.name || "----"}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-200/40">
                          <span className="font-medium text-gray-500">Timing</span>
                          <span className="font-semibold text-gray-800">{settingsData?.shift_details?.timing || "----"}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="font-medium text-gray-500">Weekly Off</span>
                          <span className="font-semibold text-gray-800">{settingsData?.shift_details?.weekly_off || "----"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Department Info */}
                    <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                      <h5 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                        <OfficeBuildingIcon className="w-5 h-5 text-purple-600" />
                        Department Info
                      </h5>
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex justify-between py-1 border-b border-gray-200/40">
                          <span className="font-medium text-gray-500">Department Name</span>
                          <span className="font-semibold text-gray-800">{settingsData?.department?.name || "----"}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="font-medium text-gray-500">Reports To</span>
                          <span className="font-semibold text-gray-800">{settingsData?.department?.reports_to || "----"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Employment Type */}
                    <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                      <h5 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                        <BriefcaseIcon className="w-5 h-5 text-emerald-600" />
                        Employment Type
                      </h5>
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex justify-between py-1 border-b border-gray-200/40">
                          <span className="font-medium text-gray-500">Type</span>
                          <span className="font-semibold text-gray-800">{settingsData?.employment_type?.name || "----"}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="font-medium text-gray-500">Effective From</span>
                          <span className="font-semibold text-gray-800">{settingsData?.employment_type?.effective_from || "----"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Security Status */}
                    <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50">
                      <h5 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                        <LockClosedIcon className="w-5 h-5 text-amber-600" />
                        Password & Security
                      </h5>
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex justify-between py-1">
                          <span className="font-medium text-gray-500">Password Set</span>
                          <span className="font-semibold text-gray-800">{settingsData?.password_and_security?.password_set || "----"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Salary Details Metadata */}
                    {settingsData?.salary_details && (
                      <div className="border border-gray-100 p-4 rounded-lg bg-gray-50/50 col-span-1 md:col-span-2">
                        <h5 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                          <CurrencyRupeeIcon className="w-5 h-5 text-indigo-600" />
                          Core Salary Metadata
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-gray-600">
                          <div className="flex justify-between py-1 border-b border-gray-200/40">
                            <span className="font-medium text-gray-500">Annual CTC</span>
                            <span className="font-semibold text-gray-800">
                              ₹{(settingsData.salary_details.annual_ctc ?? 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-gray-200/40">
                            <span className="font-medium text-gray-500">Monthly In-Hand</span>
                            <span className="font-semibold text-gray-800">
                              ₹{(settingsData.salary_details.monthly_in_hand ?? 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* MODALS */}
      <EmploymentTypeModal
        onClose={() => setIsEmploymentTypeModalOpen(false)}
        open={isEmploymentTypeModalOpen}
      />
      <AdvanceDepartmentEditing
        onClose={() => setIsOpenAdvanceDepartment(false)}
        open={isOpenAdvanceDepartment}
      />
      <AdvanceEmploymentEditing
        onClose={() => setIsOpenAdvanceEmployment(false)}
        open={isOpenAdvanceEmployment}
      />
      <EmployeeServicesModal
        open={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
      />
      <GeneratePasswordModal
        open={isGeneratePasswordModalOpen}
        company_employee_id={company_employee_id}
        onClose={() => setIsGeneratePasswordModalOpen(false)}
      />
    </>
  );
};

export default SingleEmployeeDetail;
